import { Spin } from "antd";
import Detail from "../../../components/Details/Detail";
import ReferenceLinksDetailView from "../../../components/Details/ReferenceLinksDetailView";
import { useSelector } from "react-redux";
import {
  LABEL_TXT,
  ERROR_MSGS,
  STATUS,
} from "../../../constants/constants";
import {
  capitalizeWords,
  formatCurrency,
  phoneNumberRules,
  websiteRules,
  emailRules,
} from "../../../helpers/helpers";
import { PERM_CODE } from "../../../constants/PermissionCodes";
import {
  isInvoiceStyleCategory,
  isVenturesCategory,
} from "../../../helpers/dealCategoryProducts";

const EnquiryDetails = ({ businessDetailesData, orgId, status, readOnly = false }) => {
  const { user } = useSelector((state) => state.auth);
  const { loading } = useSelector((state) => state?.business);
  const { dropdownData } = useSelector((state) => state?.enqMngt);

  const userPrivileges =
    user?.privilege?.map((p) => p.privilegeCode) || [];

  const editAccess =
    !readOnly &&
    ((userPrivileges.includes(PERM_CODE.BAEDT) &&
      status === STATUS.ACTIVE.ID) ||
    (userPrivileges.includes(PERM_CODE.BDEDT) &&
      status === STATUS.DEACTIVATED.ID) ||
    (userPrivileges.includes(PERM_CODE.BLEDT) &&
      status === STATUS.DELINQUENT.ID) ||
    (userPrivileges.includes(PERM_CODE.BBEDT) &&
      status === STATUS.BLACKLISTED.ID) ||
    (userPrivileges.includes(PERM_CODE.BIEDT) &&
      status === STATUS.INACTIVE.ID));

  const dealCategoryId =
    businessDetailesData?.busDetails?.dealCategoryId;

  const isInvoiceFinancing = isInvoiceStyleCategory(dealCategoryId);
  const isJointVenture = isVenturesCategory(dealCategoryId);


  return (
    <div className="details-card-container">
      <div className="details-card">
        <Spin spinning={loading}>
          <div className="details-content">
            {/* Common Fields */}

            <Detail
              label={LABEL_TXT.DEAL_CAT}
              value={businessDetailesData?.busDetails?.dealCategory}
            />

            <Detail
              label={LABEL_TXT.BUS_NAME}
              value={businessDetailesData?.busDetails?.busName}
              isVerified={
                businessDetailesData?.kycDetails?.kycPanValue?.isVerified
              }
            />

            <Detail
              label={LABEL_TXT.PAN}
              value={businessDetailesData?.busDetails?.pan}
            />

            <Detail
              label={LABEL_TXT.GSTIN}
              value={businessDetailesData?.busDetails?.gstin}
            />

            <Detail
              label={LABEL_TXT.POC_NAME}
              value={businessDetailesData?.userDetails?.contactName}
            />

            <Detail
              label={LABEL_TXT.POC_EMAIL}
              value={businessDetailesData?.userDetails?.email}
              name="email"
              placeholder="Enter POC Email"
              rules={emailRules("POC")}
              isVerified={
                businessDetailesData?.kycDetails?.kycEmail?.isVerified
              }
              isEditable={editAccess}
              isInput={true}
              orgId={orgId}
              isBus={true}
              modifiedAt={businessDetailesData?.modifiedAt}
            />

            <Detail
              label={LABEL_TXT.POC_PHONE}
              value={businessDetailesData?.userDetails?.phoneNumber}
              name="phoneNumber"
              placeholder="Enter Phone Number"
              rules={phoneNumberRules("POC")}
              isVerified={
                businessDetailesData?.kycDetails?.kycMobile?.isVerified
              }
              isEditable={editAccess}
              isInput={true}
              orgId={orgId}
              isBus={true}
              modifiedAt={businessDetailesData?.modifiedAt}
            />

            <Detail
              label={LABEL_TXT.BUS_WEB}
              value={businessDetailesData?.busDetails?.website?.replace(
                /^https?:\/\//i,
                ""
              )}
              name="website"
              placeholder="Enter Business Website"
              rules={websiteRules}
              isEditable={editAccess}
              isInput={true}
              orgId={orgId}
              isBus={true}
              modifiedAt={businessDetailesData?.modifiedAt}
            />

            <Detail
              label={LABEL_TXT.IND_TYPE}
              value={businessDetailesData?.busDetails?.industryTypeId}
              displayValue={
                businessDetailesData?.busDetails?.industryTypeName
              }
              name="industryTypeId"
              placeholder="Select Industry Type"
              options={dropdownData?.industryTypeList?.map(
                (industry) => ({
                  value: industry.industryTypeId,
                  label: capitalizeWords(
                    industry.industryTypeName
                  ),
                })
              )}
              rules={[
                {
                  required: true,
                  message: ERROR_MSGS.SEL_IND_TYPE,
                },
              ]}
              isEditable={editAccess}
              orgId={orgId}
              isBus={true}
              modifiedAt={businessDetailesData?.modifiedAt}
            />

            <Detail
              label={LABEL_TXT.BUS_TYPE}
              value={
                businessDetailesData?.busDetails?.businessTypeName
              }
            />

            {/* Invoice Financing Fields */}

            {isInvoiceFinancing && (
              <>
                <Detail
                  label={LABEL_TXT.ANN_TURN}
                  value={
                    businessDetailesData?.busDetails
                      ?.annualTurnoverId
                  }
                  displayValue={
                    businessDetailesData?.busDetails
                      ?.annualTurnoverName
                  }
                  name="annualTurnoverId"
                  placeholder="Select Annual Turnover"
                  options={dropdownData?.annualTurnoverList?.map(
                    (item) => ({
                      value: item.annualTurnoverId,
                      label: capitalizeWords(
                        item.annualTurnoverName
                      ),
                    })
                  )}
                  rules={[
                    {
                      required: true,
                      message: ERROR_MSGS.SEL_ANN_TURN,
                    },
                  ]}
                  isEditable={editAccess}
                  orgId={orgId}
                  isBus={true}
                  modifiedAt={businessDetailesData?.modifiedAt}
                />

                <Detail
                  label="Credit Tenure (Days)"
                  value={
                    businessDetailesData?.busDetails
                      ?.creditTenure
                  }
                />

                <Detail
                  label="Expected Amount"
                  value={formatCurrency(
                    businessDetailesData?.busDetails?.requestedCreditAmt ||
                    businessDetailesData?.busDetails?.expectedCredit ||
                    businessDetailesData?.expectedCredit ||
                    businessDetailesData?.busDetails?.amountToRaise
                  )}
                />
              </>
            )}

            {/* Joint Venture Fields */}

            {isJointVenture && (
              <>
                <Detail
                  label="Amount To Be Raised"
                  value={formatCurrency(
                    businessDetailesData?.busDetails
                      ?.requestedCreditAmt
                  )}
                />

                <Detail
                  label="Others Terms & Conditions"
                  value={
                    businessDetailesData?.busDetails
                      ?.otherTermsConditions
                  }
                />

                <Detail
                  label="Deal Details"
                  value={
                    businessDetailesData?.busDetails?.dealDetails
                  }
                />
                <ReferenceLinksDetailView
                  referenceLinks={
                    businessDetailesData?.busDetails
                      ?.referenceLinks
                  }
                />
                <Detail
                  label="Deal Upload Document"
                  value={
                    businessDetailesData?.busDetails?.pitchDeck ? (
                      <a
                        href={businessDetailesData.busDetails.pitchDeck}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        View Document
                      </a>
                    ) : (
                      "-"
                    )
                  }
                />
              </>
            )}
          </div>
        </Spin>
      </div>
    </div>
  );
};

export default EnquiryDetails;